import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { Box, Button, Grid, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import InputTextSearch from '../../components/form/InputTextSearch';
import { VesselContext } from '../../context/vesselContext';
import { NotificationContext } from '../../context/notificationContext';
import { defaultPagination } from '../../utils/constants/defaults';
import paths from '../../utils/constants/paths';
import { debounce } from '../../utils/lib';
import getVesselsGridColumns from './ListGridColumns';
import VesselForm from './VesselForm';
import './Vessels.scss';
import { Pagination } from '../../utils/types/pagination';
import { MRT_PaginationState, MRT_RowSelectionState, MRT_SortingState } from 'material-react-table';
import MrtDataTable from '../../components/mrt-data-table';
import { getInactiveVesselRowProps } from '../../utils/tableRowStyles';
import { VesselInterface } from '../../utils/types/vessel';

type VesselStatusTab = 'all' | 'active' | 'inactive';

const VESSEL_STATUS_TABS: { id: VesselStatusTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' }
];

const defaultAttributesParams = ['id', 'active', 'hullNumber', 'name'];
const defaultOrderParams = ['createdAt:DESC'];
const defaultPaginationParams = {
  ...defaultPagination,
  attributes: defaultAttributesParams,
  order: defaultOrderParams,
  join: ['owner']
};

const getActiveFilter = (tab: VesselStatusTab) => {
  if (tab === 'active') return 'active:true';
  if (tab === 'inactive') return 'active:false';
  return '';
};

const Vessels: FC = (): ReactElement => {
  const navigate = useNavigate();
  const { listVesselsPaginated, bulkUpdateVesselsActive, state } = useContext(VesselContext);
  const { showAlert } = useContext(NotificationContext);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<VesselStatusTab>('all');
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [paginationParams, setPaginationParams] = useState<Pagination>(defaultPaginationParams);

  const selectedIds = useMemo(() => Object.keys(rowSelection), [rowSelection]);
  const hasSelection = selectedIds.length > 0;

  useEffect(() => {
    document.body.style.backgroundColor = '#E7E7E9';

    return () => {
      document.body.style.backgroundColor = '#FFF';
    };
  }, []);

  useEffect(() => {
    if (state.create.done) listVesselsPaginated(paginationParams);
  }, [state.create]);

  useEffect(() => {
    if (state.bulkUpdateActive.done) {
      showAlert({
        variant: 'success',
        title: 'Vessels',
        description: 'Vessels updated successfully'
      });
      setRowSelection({});
      listVesselsPaginated(paginationParams);
    } else if (state.bulkUpdateActive.error) {
      showAlert({
        variant: 'error',
        title: 'Vessels',
        description: state.bulkUpdateActive.error.text ?? 'Error updating vessels'
      });
    }
  }, [state.bulkUpdateActive.done, state.bulkUpdateActive.error]);

  const onEditVessel = (vessel: VesselInterface) => {
    navigate({
      pathname: paths.VESSEL_SECTIONS.absolutePath,
      search: `id=${vessel.id}`
    });
  };

  const columns = useMemo(() => getVesselsGridColumns(onEditVessel), [onEditVessel]);

  const toggleDrawer = (isOpen: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setIsFormOpen(isOpen);
  };

  useEffect(() => {
    listVessels(paginationParams, isFormOpen);
  }, [paginationParams, isFormOpen]);

  const handleSearch = debounce((inputVal) => {
    setPaginationParams((prevParams) => ({
      ...prevParams,
      page: 1,
      search: `name:${inputVal},hullNumber:${inputVal}`
    }));
  }, 500);

  const listVessels = useCallback(
    (paginationParams: Pagination, isFormOpen: boolean) => {
      if (!isFormOpen) listVesselsPaginated(paginationParams);
    },
    [listVesselsPaginated]
  );

  const handlePaginationChange = (paginationState: MRT_PaginationState) => {
    setPaginationParams((prevParams) => ({
      ...prevParams,
      page: paginationState.pageIndex + 1,
      perPage: paginationState.pageSize
    }));
    setRowSelection({});
  };

  const handleSortingChange = (sortingState: MRT_SortingState) => {
    const order = sortingState.length
      ? sortingState.map((sort) => `${sort.id}:${sort.desc ? 'DESC' : 'ASC'}`)
      : defaultOrderParams;

    setPaginationParams((prevParams) => ({
      ...prevParams,
      order
    }));
  };

  const handleTabChange = (tab: VesselStatusTab) => {
    setActiveTab(tab);
    setRowSelection({});
    setPaginationParams((prevParams) => ({
      ...prevParams,
      page: 1,
      filter: getActiveFilter(tab)
    }));
  };

  const handleBulkAction = (active: boolean) => {
    setBulkMenuAnchor(null);
    bulkUpdateVesselsActive({ ids: selectedIds, active });
  };

  const handlerCloseForm = async () => {
    setIsFormOpen(false);
  };

  return (
    <>
      <Grid container className="vessels-list-header" justifyContent="space-between" alignItems="center">
        <Grid item xs={12} md={4} className="vessels-header-title">
          <span className="table-title">Vessels list</span>
          <div className="vessels-tabs">
            {VESSEL_STATUS_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`vessels-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}>
                {tab.label}
              </button>
            ))}
          </div>
        </Grid>

        <Grid item xs={12} md={8} className="vessels-header-actions-col">
          <div className="vessels-header-actions">
            <Button
              variant="outlined"
              className="bulk-actions-button"
              disabled={!hasSelection || state.bulkUpdateActive.loading}
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(event) => setBulkMenuAnchor(event.currentTarget)}>
              Bulk actions
            </Button>
            <Menu
              anchorEl={bulkMenuAnchor}
              open={Boolean(bulkMenuAnchor)}
              onClose={() => setBulkMenuAnchor(null)}>
              <MenuItem onClick={() => handleBulkAction(true)}>Enable</MenuItem>
              <MenuItem onClick={() => handleBulkAction(false)}>Disable</MenuItem>
            </Menu>
            <Box className="vessels-header-search">
              <InputTextSearch
                value={searchValue}
                placeholder="Search vessel"
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  handleSearch(e.target.value);
                }}
              />
            </Box>
            <Button
              variant="contained"
              type="button"
              className="new-vessel-button"
              onClick={toggleDrawer(!isFormOpen)}>
              New vessel
            </Button>
          </div>
        </Grid>
      </Grid>

      <MrtDataTable
        isLoading={state?.list?.loading}
        isError={Boolean(state?.list?.error)}
        columns={columns}
        rows={state?.list?.data?.results}
        rowCount={state?.list?.data?.pagination?.total}
        initialPagination={{
          pageIndex: paginationParams.page - 1,
          pageSize: paginationParams.perPage
        }}
        onPaginationChange={handlePaginationChange}
        onSortingChange={handleSortingChange}
        muiTableBodyRowProps={({ row }) => getInactiveVesselRowProps(row)}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        getRowId={(row) => row.id}
      />

      <SwipeableDrawer
        anchor={'bottom'}
        open={isFormOpen}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        sx={swipeableDrawerStyle}>
        {isFormOpen && <VesselForm onClickClose={handlerCloseForm} />}
      </SwipeableDrawer>
    </>
  );
};

export default Vessels;

const swipeableDrawerStyle = {
  maxHeight: 'calc(100% - 64px)',
  '.MuiPaper-root': {
    background: '#fff',
    borderRadius: '8px 8px 0 0'
  }
};
